import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Beaker, ArrowRight, Calendar, User, Tag, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { API_BASE } from '@/lib/api';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';

export function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_BASE}/blog/posts`).then(r => setPosts(r.data.posts || [])).catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <div className="">

      <section className="pt-28 pb-16 px-6">
        <div className="max-w-[900px] mx-auto">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Blog</h1>
          <p className="text-muted-foreground mb-8">Updates, tutorials, and insights from the Pharmax.ai team</p>

          {loading ? (
            <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
          ) : (
            <div className="space-y-4">
              {posts.map((post, i) => (
                <motion.div key={post.id || i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
                  <Card className="card-hover cursor-pointer" onClick={() => navigate(`/blog/${post.slug}`)}>
                    <CardContent className="py-5 px-6">
                      <div className="flex items-center gap-2 mb-2">
                        {post.tags?.slice(0, 3).map(t => (
                          <span key={t} className="text-[9px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">{t}</span>
                        ))}
                      </div>
                      <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{post.title}</h2>
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{post.excerpt}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><User className="w-3 h-3" /> {post.author}</span>
                        {post.published_at && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(post.published_at).toLocaleDateString()}</span>}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_BASE}/blog/posts/${slug}`).then(r => setPost(r.data)).catch(() => navigate('/blog')).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Skeleton className="h-8 w-48" /></div>;
  if (!post) return null;

  return (
    <div className="">

      <article className="pt-24 pb-16 px-6">
        <div className="max-w-[750px] mx-auto">
          {/* SEO title in document head would go here in production */}
          <div className="flex items-center gap-2 mb-3">
            {post.tags?.map(t => <span key={t} className="text-[9px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">{t}</span>)}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>{post.title}</h1>
          <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {post.author}</span>
            {post.published_at && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {new Date(post.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>}
          </div>
          <div className="mt-8">
            <MarkdownRenderer content={post.content_md} />
          </div>
        </div>
      </article>
    </div>
  );
}
